import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Send, Sparkles, User, Info, X, Copy, 
  CheckCircle, XCircle, Target, Loader2, AlertCircle,
  Edit3, Eye, FileText, RefreshCw, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../utils/auth';
import DeclineModal from '../components/DeclineModal';
import ComplexOfferModal from '../components/ComplexOfferModal';
import { formatResponse, formatChatMessage } from '../utils/formatResponse';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { sanitizeAIHTML, sanitizeUserHTML } from '../utils/sanitizer';
import { casesApi, aiApi } from '../services';

// Use relative URL - works on ANY domain automatically!
const API = '/api';

const MarketplaceWorkspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = auth.getUser();
  
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseMessage, setResponseMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showCaseInfo, setShowCaseInfo] = useState(false);
  
  // Chat with AI assistant
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  
  // Assessment modals
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showComplexModal, setShowComplexModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [focusMode, setFocusMode] = useState(false);

  useEffect(() => {
    const allowedRoles = ['LAWYER', 'ADMIN', 'TENANT_ADMIN'];
    if (!user || !allowedRoles.includes(user.role)) {
      navigate('/lexy');
      return;
    }
    fetchCase();
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat]);

  const fetchCase = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/cases/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCaseData(response.data);
      
      // Check if AI draft already exists (auto-generated when lawyer took the case)
      if (response.data.ai_draft_response) {
        console.log('✅ Pre-generated AI draft found!');
        setResponseMessage(response.data.ai_draft_response);
      } else if (response.data.ai_draft) {
        // Fallback for old field name
        console.log('✅ AI draft found (old format)');
        setResponseMessage(response.data.ai_draft);
      } else {
        // No draft exists - generate now
        console.log('⚠️ No AI draft found, generating now...');
        generateAIResponse(response.data);
      }
      
      // Initialize chat with case info
      setChat([{
        role: 'assistant',
        content: `**SAK:** ${response.data.title || response.data.topic}

**BESKRIVELSE FRA KLIENT:**
${response.data.description || response.data.intake_summary}

────────────

Hei! Jeg er din juridiske AI-assistent. 

Jeg har allerede generert et utkast til svar som du kan se til høyre. Du kan:
- Diskutere saken med meg
- Be om forbedringer av svaret
- Sjekke lovverk
- Få forslag til juridiske argumenter`,
        timestamp: new Date()
      }]);
      
    } catch (error) {
      console.error('Error fetching case:', error);
      toast.error('Kunne ikke laste sak');
      navigate('/lexy/dashboard?tab=marketplace');
    } finally {
      setLoading(false);
    }
  };

  const generateAIResponse = async (caseInfo) => {
    setIsGenerating(true);
    setResponseMessage('Genererer juridisk svar med AI...\n\nDette tar vanligvis 10-30 sekunder.');
    
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/marketplace/generate-draft`,
        {
          case_id: caseInfo.id,
          topic: caseInfo.title || caseInfo.topic || 'Juridisk sak',
          intake_summary: caseInfo.description || caseInfo.intake_summary || '',
          complexity: 'MEDIUM'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResponseMessage(response.data.draft);
      toast.success('AI-svar generert!');
      
    } catch (error) {
      console.error('AI generation error:', error);
      setResponseMessage('Kunne ikke generere AI-svar. Skriv ditt eget svar her...');
      toast.error('AI-generering feilet');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isTyping) return;

    const userMessage = input.trim();
    setInput('');
    
    setChat(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);

    setIsTyping(true);

    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/chat/lawyer-assistant`,
        {
          case_id: id,
          message: userMessage,
          current_document: responseMessage,
          conversation_history: chat.slice(-4).map(m => ({
            role: m.role,
            content: m.content
          }))
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );

      setChat(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date()
      }]);

      // If AI modified document, update it
      if (response.data.updated_document) {
        setResponseMessage(response.data.updated_document);
        toast.success('Dokumentet er oppdatert!');
      }

    } catch (error) {
      console.error('Chat error:', error);
      setChat(prev => [...prev, {
        role: 'assistant',
        content: '⚠️ Beklager, noe gikk galt. Prøv igjen.',
        error: true,
        timestamp: new Date()
      }]);
      toast.error('Kunne ikke sende melding');
    } finally {
      setIsTyping(false);
    }
  };

  const handleDecline = async (reason) => {
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/marketplace/cases/${id}/decline`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Saken er avslått');
      navigate('/lexy/dashboard?tab=marketplace');
      
    } catch (error) {
      console.error('Decline error:', error);
      toast.error('Kunne ikke avslå saken');
    }
  };

  const handleSendOffer = async (offerData) => {
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/marketplace/cases/${id}/send-offer`,
        offerData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Tilbud sendt til kunde!');
      navigate('/lexy/dashboard?tab=marketplace');
      
    } catch (error) {
      console.error('Send offer error:', error);
      toast.error('Kunne ikke sende tilbud');
    }
  };

  const handleSendSimpleAnswer = async () => {
    if (!responseMessage.trim()) {
      toast.error('Svaret kan ikke være tomt');
      return;
    }

    setIsSending(true);
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/marketplace/send-answer`,
        {
          case_id: id,
          answer_text: responseMessage,
          ai_draft: responseMessage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Svar sendt til kunde! Venter på betaling.');
      navigate('/lexy/dashboard?tab=marketplace');
      
    } catch (error) {
      console.error('Send answer error:', error);
      toast.error('Kunne ikke sende svar');
    } finally {
      setIsSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Sak ikke funnet</p>
          <Button onClick={() => navigate('/lexy/dashboard?tab=marketplace')}>
            Tilbake til marketplace
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Linear clean style */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-20">
        <div className="px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={() => navigate('/lexy/dashboard?tab=marketplace')}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors text-sm font-medium rounded-md border border-slate-200"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Tilbake</span>
              </button>
              <div className="h-4 w-px bg-slate-200 hidden sm:block"></div>
              <div>
                <h1 className="text-sm sm:text-base font-medium text-slate-900">
                  {caseData.title || caseData.topic}
                </h1>
                <p className="text-xs text-slate-500">
                  {caseData.client_name || 'Kunde'} · {caseData.category}
                </p>
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCaseInfo(!showCaseInfo)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 transition-colors text-xs sm:text-sm font-medium rounded-md ${
                  showCaseInfo
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Info className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Info</span>
              </button>
              <button
                onClick={() => setShowAIPanel(!showAIPanel)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 transition-colors text-xs sm:text-sm font-medium rounded-md ${
                  showAIPanel
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">AI</span>
              </button>
              <button
                onClick={() => setFocusMode(!focusMode)}
                className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-md text-sm font-medium transition-colors"
              >
                <Target className="w-3.5 h-3.5" />
                {focusMode ? 'Normal' : 'Fokus'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Case Info Dropdown - Linear style */}
      {showCaseInfo && !focusMode && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 w-full max-w-2xl z-30 px-4 animate-in slide-in-from-top-2 fade-in duration-200">
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg p-5">
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-medium text-slate-900 text-sm">Saksinformasjon</h3>
              <button
                onClick={() => setShowCaseInfo(false)}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1.5">Beskrivelse</p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {caseData.description || caseData.intake_summary}
                </p>
              </div>
              {caseData.uploaded_documents && caseData.uploaded_documents.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1.5">Dokumenter</p>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <FileText className="w-4 h-4" />
                    <span>{caseData.uploaded_documents.length} dokumenter</span>
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1.5">Kategori</p>
                <p className="text-sm text-slate-700">{caseData.category} · {caseData.subcategory}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Full width editor or split view */}
      <main className={`transition-all duration-300 ${focusMode ? 'pt-0' : 'pt-24'}`}>
        <div className={`${focusMode ? 'h-screen' : 'h-[calc(100vh-6rem)]'} flex`}>
          
          {/* Main Editor Area */}
          <div className={`flex flex-col bg-white transition-all duration-300 ${
            showAIPanel && !focusMode ? 'w-[65%]' : 'w-full'
          }`}>
            
            {!focusMode && (
              <div className="px-4 sm:px-6 lg:px-8 py-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium text-slate-900">Svar til kunde</h2>
                  <div className="flex items-center gap-2">
                    {isGenerating && (
                      <span className="flex items-center gap-2 text-sm text-purple-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Genererer...
                      </span>
                    )}
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                        isEditing
                          ? 'bg-slate-900 text-white'
                          : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {isEditing ? (
                        <>
                          <Eye className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Forhåndsvisning</span>
                          <span className="sm:hidden">Preview</span>
                        </>
                      ) : (
                        <>
                          <Edit3 className="w-3.5 h-3.5" />
                          Rediger
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Editor / Preview */}
            <div className="flex-1 overflow-y-auto">
              {isGenerating ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-slate-600 animate-pulse" />
                  </div>
                  <p className="text-base font-medium text-slate-900">Genererer juridisk svar...</p>
                  <p className="text-sm text-slate-500">AI analyserer saken</p>
                </div>
              ) : isEditing ? (
                <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto h-full overflow-auto">
                  <style>{`
                    .marketplace-editor .ql-toolbar {
                      border: none;
                      border-bottom: 1px solid #e2e8f0;
                      background: #f8fafc;
                      padding: 12px;
                      border-radius: 8px 8px 0 0;
                    }
                    .marketplace-editor .ql-container {
                      border: 1px solid #e2e8f0;
                      border-radius: 0 0 8px 8px;
                      font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif;
                      font-size: 15px;
                      background: white;
                    }
                    .marketplace-editor .ql-editor {
                      min-height: 500px;
                      padding: 24px 32px;
                      line-height: 1.7;
                    }
                    .marketplace-editor .ql-editor.ql-blank::before {
                      color: #94a3b8;
                      font-style: normal;
                      left: 32px;
                    }
                    .marketplace-editor .ql-editor p {
                      margin-bottom: 1em;
                    }
                    .marketplace-editor .ql-editor h2 {
                      font-size: 1.5em;
                      font-weight: 700;
                      margin-top: 1.5em;
                      margin-bottom: 0.5em;
                      color: #0f172a;
                    }
                    .marketplace-editor .ql-editor h3 {
                      font-size: 1.25em;
                      font-weight: 600;
                      margin-top: 1.25em;
                      margin-bottom: 0.5em;
                      color: #0f172a;
                    }
                    .marketplace-editor .ql-editor ul, .marketplace-editor .ql-editor ol {
                      padding-left: 1.5em;
                      margin-bottom: 1em;
                    }
                    .marketplace-editor .ql-editor strong {
                      font-weight: 600;
                    }
                    .marketplace-editor .ql-editor a {
                      color: #3b82f6;
                      text-decoration: underline;
                    }
                  `}</style>
                  <ReactQuill
                    value={responseMessage}
                    onChange={setResponseMessage}
                    placeholder="Rediger svaret ditt her. Bruk formatering for å fremheve viktige punkter..."
                    theme="snow"
                    className="marketplace-editor"
                    modules={{
                      toolbar: [
                        [{ 'header': [2, 3, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                  />
                </div>
              ) : (
                <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto h-full overflow-auto">
                  {/* AI Badge - Linear style */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">
                        <Sparkles className="w-3 h-3" />
                        <span>AI-generert</span>
                      </div>
                      <span className="text-xs text-slate-400 hidden sm:inline">GPT-5</span>
                    </div>
                    <button
                      onClick={() => generateAIResponse(caseData)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded transition-colors"
                    >
                      <RefreshCw className="w-3 h-3" />
                      <span className="hidden sm:inline">Regenerer</span>
                    </button>
                  </div>
                  
                  {/* Response Card - Linear clean style */}
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    {/* Content */}
                    <div className="p-6 sm:p-8" style={{ minHeight: '500px' }}>
                      <div 
                        className="formatted-response text-base leading-relaxed text-slate-700"
                        style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                          lineHeight: '1.7'
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeAIHTML(formatResponse(responseMessage)) }}
                      />
                    </div>
                    
                    {/* Footer with metadata */}
                    <div className="px-6 sm:px-8 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {responseMessage.split(' ').length} ord
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(responseMessage);
                          toast.success('Kopiert!');
                        }}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-white rounded transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                        Kopier
                      </button>
                    </div>
                  </div>
                  
                  {/* Quick Tips - Linear style */}
                  <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Zap className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-slate-600">
                        <span className="font-medium">Tips:</span> Trykk "Rediger" for å tilpasse svaret, eller bruk AI Assistent.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* AI Panel - Slides in from right */}
          {showAIPanel && !focusMode && (
            <div className="w-[35%] flex flex-col bg-gradient-to-b from-slate-50 to-white border-l border-slate-200 animate-in slide-in-from-right duration-300">
              {/* AI Header */}
              <div className="px-6 py-4 border-b border-slate-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-slate-900">AI Assistent</h2>
                      <p className="text-xs text-slate-500">Online • GPT-5</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAIPanel(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-slate-600" />
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="space-y-4">
                  {chat.map((msg, i) => (
                    <div
                      key={i}
                      className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                      )}
                      
                      <div className="flex flex-col max-w-[85%]">
                        <div className={`px-4 py-3 rounded-2xl ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-lg rounded-tr-sm'
                            : 'bg-white text-slate-800 border border-slate-200 shadow-md rounded-tl-sm hover:shadow-lg transition-shadow'
                        }`}>
                          <div 
                            className="text-sm leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: sanitizeAIHTML(formatChatMessage(msg.content)) }}
                          />
                        </div>
                        <span className="text-xs text-slate-400 mt-1 px-2">
                          {msg.timestamp && new Date(msg.timestamp).toLocaleTimeString('no-NO', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>

                      {msg.role === 'user' && (
                        <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  ))}

                  {isTyping && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                      <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-md">
                        <div className="flex gap-1.5">
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={chatEndRef} />
                </div>
              </div>

              {/* Chat Input */}
              <div className="p-4 bg-white border-t border-slate-200">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Diskuter saken..."
                    className="flex-1 border-2 border-slate-300 focus:border-purple-400 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all text-sm"
                  />
                  <Button
                    type="submit"
                    disabled={!input.trim() || isTyping}
                    className="bg-purple-600 hover:bg-purple-700 px-5"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Assessment Bar - Bottom - Linear clean style */}
      {!focusMode && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-20">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            {/* Info bar */}
            <div className="max-w-5xl mx-auto mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                <span className="hidden sm:inline">Klar til å sende</span>
              </div>
              <div className="text-xs text-slate-400 hidden md:inline">
                ⌘ + Enter
              </div>
            </div>
            
            {/* Action Buttons - Linear style */}
            <div className="flex gap-2 max-w-5xl mx-auto">
              {/* Decline */}
              <button
                onClick={() => setShowDeclineModal(true)}
                className="px-4 py-2.5 border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-red-600 hover:border-red-200 font-medium rounded-md transition-colors flex items-center gap-2 text-sm"
              >
                <XCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Avslå</span>
              </button>
              
              {/* Send Simple Answer - PRIMARY */}
              <button
                onClick={handleSendSimpleAnswer}
                disabled={isSending || !responseMessage.trim()}
                className="flex-1 px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              >
                {isSending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sender...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Send svar (499 kr)</span>
                  </>
                )}
              </button>
              
              {/* Complex Offer */}
              <button
                onClick={() => setShowComplexModal(true)}
                className="px-4 py-2.5 border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-md transition-colors flex items-center gap-2 text-sm"
              >
                <Target className="w-4 h-4" />
                <span className="hidden sm:inline">Kompleks sak</span>
                <span className="sm:hidden">Kompleks</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <DeclineModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        onDecline={handleDecline}
        caseName={caseData.title || caseData.topic}
      />

      <ComplexOfferModal
        isOpen={showComplexModal}
        onClose={() => setShowComplexModal(false)}
        onSendOffer={handleSendOffer}
        caseData={caseData}
      />
    </div>
  );
};

export default MarketplaceWorkspace;
